import React, { useState, useEffect } from 'react'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import EmptyTable from '../EmptyTable'
import { useAuth } from "../api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import { HistoryMobileDateRangePicker } from '../HistoryDateRangePickers'
import MobilePaginationActions from './MobilePaginationActions'

const getTransactionMemo = (row) => {
    if (row?.memo) return row.memo
    if (row?.rental) {
        if (row.type === 'rental') {
            return `leased proxy ${row.rental.ip || ''} ${row.rental.country || ''} ${row.rental.rentalid}`.trim()
        }
        if (row.type === 'renewal') {
            return `renewed lease ID ${row.rental.rentalid}`
        }
        if (row.type === 'refund') {
            return `refund for lease ${row.rental.rentalid}`
        }
    }
    return ''
}


// Компонент чипа для типа транзакции
const TransactionChip = ({ type }) => {
    const getChipStyle = (type) => {
        switch (type) {
            case 'lease':
            case 'rental':
            case 'renewal':
                return {
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    color: '#FF9800',
                    border: '1px solid #FF9800'
                }
            case 'refund':
                return {
                    backgroundColor: 'rgba(0, 178, 74, 0.1)',
                    color: '#00B24A',
                    border: '1px solid #00B24A'
                }
            case 'deposit':
                return {
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    color: '#1976D2',
                    border: '1px solid #1976D2'
                }
            default:
                return {
                    backgroundColor: 'var(--medium-gray)',
                    color: 'var(--text-muted)',
                    border: '1px solid var(--light-gray)'
                }
        }
    }

    return (
        <span style={{
            ...getChipStyle(type),
            padding: '6px 12px',
            borderRadius: '7px',
            fontSize: 'var(--text-xs)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100px',
            height: '35px',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {type}
        </span>
    )
}


const MobilePaymentsTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [paymentsList, setPaymentsList] = useState([])
    const [startDate, setStartDate] = useState(() => dayjs())
    const [endDate, setEndDate] = useState(() => dayjs().endOf('month'))
    const [showFilters, setShowFilters] = useState(false)


    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - paymentsList?.balanceRecords?.length) : 0

    const handleChangePage = React.useCallback((event, newPage) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        setPage(validPage)
    }, [])

    const handleChangeRowsPerPage = React.useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }, [])

    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD')
            const dateEndFormated = endDate.format('YYYY-MM-DD')
            
            // Получаем токен один раз
            const token = await getAccessTokenSilently()
            
            const paymentsPesponse = await props.apiClient.setToken(token).getUserPayments(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setPaymentsList(paymentsPesponse)
          } catch (e) {
          }
      }
     
      callAsync()   
    },[startDate, endDate, page, rowsPerPage, getAccessTokenSilently])

    const handleDateRangeChange = React.useCallback((start, end) => {
      setStartDate(start)
      setEndDate(end)
    }, [])

    const getPaymentsDefaultRange = React.useCallback(() => ({
      start: dayjs().startOf('day'),
      end: dayjs().endOf('month')
    }), [])

    return (
        <div style={{
            width: '100%',
            height: '100%',
            padding: 'var(--spacing-sm)',
            background: 'var(--black)',
            overflow: 'auto'
        }}>
            
            {/* Мобильные фильтры по дате */}
            <div className="mobile-filters">
                <div 
                    className="mobile-filters-header"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <span className="mobile-filters-title">Date Filters</span>
                    <span style={{ 
                        color: 'var(--text-muted)',
                        transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ▼
                    </span>
                </div>
                
                {showFilters && (
                    <div className="mobile-filters-content">
                        <div className="mobile-filters-grid" style={{ gridTemplateColumns: '1fr' }}>
                            <HistoryMobileDateRangePicker
                                label="Date range"
                                startDate={startDate}
                                endDate={endDate}
                                onRangeChange={handleDateRangeChange}
                                getResetRange={getPaymentsDefaultRange}
                            />
                        </div>
                    </div>
                )}
            </div>
            
            {paymentsList?.balanceRecords?.length > 0 ? (
                <div>
                    {/* Мобильные карточки транзакций */}
                    <div style={{ padding: '0' }}>
                        {paymentsList?.balanceRecords?.map((row, index) => (
                            <div
                                key={index}
                                className="mobile-payment-card"
                            >
                                {/* Заголовок карточки */}
                                <div className="mobile-payment-header">
                                    <div>
                                        <div className="mobile-payment-amount" style={{ 
                                            color: row?.amount < 0 ? '#eb5d0c' : '#00B24A',
                                        }}>
                                            {row?.amount < 0 ? `-$${row?.amount ? (Math.abs(row.amount)/100).toFixed(2) : '0.00'}` : `+$${row?.amount ? (row.amount/100).toFixed(2) : '0.00'}`}
                                        </div>
                                        <div className="mobile-payment-date">
                                            {moment(row?.recordedAt).format('MMM DD, YYYY')}
                                        </div>
                                    </div>
                                    <TransactionChip type={row?.type} />
                                </div>

                                {/* Детали транзакции */}
                                <div className="mobile-payment-details">
                                    <div className="mobile-payment-detail">
                                        <div className="mobile-payment-detail-label">Description</div>
                                        <div className="mobile-payment-detail-value">
                                            {getTransactionMemo(row)?.replace(/(.{60})..+/, "$1…")}
                                        </div>
                                    </div>
                                    
                                    <div className="mobile-payment-detail">
                                        <div className="mobile-payment-detail-label">Balance</div>
                                        <div className="mobile-payment-detail-value" style={{ 
                                            color: '#1A53FF',
                                            fontWeight: '600'
                                        }}>
                                            ${row?.balance ? (row.balance/100).toFixed(2) : '0.00'}
                                        </div>
                                    </div>
                                </div>


                                {/* Время транзакции */}
                                <div className="mobile-payment-time">
                                    {moment(row?.recordedAt).format('h:mm A')}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Мобильная пагинация */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-md)',
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        marginTop: 'var(--spacing-md)'
                    }}>
                        {/* Строка с количеством строк на странице */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-sm)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 8px',
                                    fontSize: 'var(--text-sm)',
                                    minWidth: '60px'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Строка с навигацией */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 'var(--spacing-sm)'
                        }}>
                            <MobilePaginationActions 
                                page={page}
                                rowsPerPage={rowsPerPage}
                                count={paymentsList?.total || 0}
                                onPageChange={handleChangePage}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">💰</div>
                    <h3 className="empty-state-title">No transactions found</h3>
                    <p className="empty-state-description">
                        You don't have any transaction records for the selected period.
                    </p>
                </div>
            )}
        </div>
    )
}

export default MobilePaymentsTable
